import React, { useState, useEffect } from "react";
import FadeAlert, {
  FadeAlertType,
} from "../../components/FadeAlert/FadeAlert.tsx";
import PolicyService from "../../services/PolicyService/PolicyService.ts";
import { useAuth } from "../../provider/AuthProvider.tsx";
import {
  normalizeDate,
  revertNormalizeDate,
} from "../../utils/NormalizationUtil.ts";
import InsuranceService from "../../services/InsuranceService/InsuranceService.ts";
import DatePicker from "../../components/Forms/DatePicker/DatePicker.tsx";
import SelectGroup from "../../components/Forms/SelectGroup/SelectGroup.tsx";
import { PolicyReferenceProps } from "../../enum/policy-fetch-type.enum.ts";
import { useLocation } from "react-router-dom";
import SectionHeader from "../../components/SectionHeader/SectionHeader.tsx";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb.tsx";
import DocumentsPPPDataGrid from "../../components/DataTables/DocumentsPPPDataGrid.tsx";
import moment from "moment/moment";

const labelClass =
  "mb-1 ml-1 block text-xs font-medium text-black dark:text-white";

export interface DocumentPPPRequest {
  fromDate: string;
  toDate: string;
  company?: string;
  policyType?: string;
  agent?: string;
  selectedOffices: string[];
}

const PPP_STATUS_OPTIONS = {
  "0": "Празни",
  "4": "Анулирани",
  "5": "Върнати в ЗК",
} as const;

type PPPStatusKey = keyof typeof PPP_STATUS_OPTIONS;

interface DocumentPPPData {
  PolNum: string;
  InsComp: string;
  DocType: string;
  DateR: string;
  Agent: string;
}

const DocumentsPPP: React.FC<PolicyReferenceProps> = () => {
  const [formState, setFormState] = useState({
    fromDate: "",
    toDate: "",
    company: "",
    policyType: "",
    agent: "",
    pppStatus: "0" as PPPStatusKey,
  });
  const [alert, setAlert] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<FadeAlertType>();
  const { jwt } = useAuth();
  const [agentOptions, setAgentOptions] = useState<{ [key: string]: string }>(
    {}
  );
  const [insuranceCompanies, setInsuranceCompanies] = useState<{
    [key: string]: string;
  }>({});
  const [policyTypes, setPolicyTypes] = useState<{ [key: string]: string }>({});
  const [documents, setDocuments] = useState<DocumentPPPData[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const location = useLocation();

  moment.locale("bg");

  const formattedDateTime = moment().format("DD/MM/YYYY HH:mm");

  const printMetadata = [
    { label: "", value: `Дата: ${formattedDateTime}` },
    { label: "", value: "ПРИЕМО-ПРЕДАВАТЕЛЕН ПРОТОКОЛ" },
    { label: "", value: "За приети документи" },
    { label: "", value: `от ${formState.fromDate} до ${formState.toDate}` },
  ];

  const printFooterMetadata = [
    {
      label: "",
      value:
        "Дата: ____________________________        Подпис: ______________________________",
    },
  ];

  useEffect(() => {
    setFormState({
      fromDate: "",
      toDate: "",
      company: "",
      policyType: "",
      agent: "",
      pppStatus: "0",
    });
    setDocuments([]);
    setAlert(null);
    setAlertType(undefined);
  }, [location.pathname]);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setIsLoading(true);
        const [agents, companies, policyTypesRes] = await Promise.all([
          InsuranceService.getAllAgents(jwt),
          InsuranceService.getAllInsuranceCompanies(jwt),
          InsuranceService.getAllPolicyTypes(jwt),
        ]);
        setAgentOptions(agents as { [key: string]: string });
        setInsuranceCompanies(companies as { [key: string]: string });
        setPolicyTypes(policyTypesRes as { [key: string]: string });
      } catch (err: any) {
        throw new Error(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [jwt]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const { fromDate, toDate, pppStatus } = formState;

    if (!fromDate || !toDate) {
      setAlert("Моля, въведете начална и крайна дата");
      setAlertType(FadeAlertType.error);
      return;
    }

    setDocuments([]);
    try {
      setIsLoading(true);
      const result = await PolicyService.fetchDocumentPPP(
        {
          fromDate: revertNormalizeDate(formState.fromDate),
          toDate: revertNormalizeDate(toDate),
          company: formState.company,
          policyType: formState.policyType,
          agent: formState.agent,
          selectedOffices: [],
        },
        pppStatus as PPPStatusKey,
        jwt
      );

      const normalizedDocuments = (result as DocumentPPPData[]).map((doc) => ({
        ...doc,
        DateR: doc.DateR ? normalizeDate(doc.DateR.trim()) : "",
      }));

      setDocuments(normalizedDocuments);

      if (normalizedDocuments.length === 0) {
        setAlert("Няма намерени документи");
        setAlertType(FadeAlertType.warning);
      }
    } catch (error) {
      setAlert("Грешка при зареждането на документи");
      setAlertType(FadeAlertType.error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Breadcrumb pageName="ППП на Документи" />
      <div className="border-stroke shadow-default dark:border-strokedark dark:bg-boxdark rounded-sm border bg-white">
        <FadeAlert
          alert={alert}
          type={alertType}
          onClose={() => setAlert(null)}
        />

        <SectionHeader title="ППП на Документи" />

        <form onSubmit={handleSearch} className="p-3">
          <div className="grid grid-cols-3 gap-2">
            <div>
              <DatePicker
                label="От дата"
                name="fromDate"
                onChange={handleInputChange}
                value={formState.fromDate}
              />
            </div>
            <div>
              <DatePicker
                label="До дата"
                name="toDate"
                onChange={handleInputChange}
                value={formState.toDate}
              />
            </div>
            <div>
              <label className={labelClass}>ЗК</label>
              <SelectGroup
                value={formState.company}
                placeholder="Всички"
                options={insuranceCompanies}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, company: value }))
                }
                name="company"
              />
            </div>
            <div>
              <label className={labelClass}>Вид Полица</label>
              <SelectGroup
                value={formState.policyType}
                placeholder="Всички"
                options={policyTypes}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, policyType: value }))
                }
                name="policyType"
              />
            </div>
            <div>
              <label className={labelClass}>Агент</label>
              <SelectGroup
                value={formState.agent}
                placeholder="Всички"
                options={agentOptions}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, agent: value }))
                }
                name="agent"
              />
            </div>
            <div>
              <label className={labelClass}>Статус</label>
              <SelectGroup
                value={formState.pppStatus}
                placeholder="Изберете статус"
                options={PPP_STATUS_OPTIONS}
                onChange={(value) =>
                  setFormState((prev) => ({
                    ...prev,
                    pppStatus: value as PPPStatusKey,
                  }))
                }
                name="pppStatus"
              />
            </div>
          </div>

          <div className="mt-2 flex justify-center">
            <button
              type="submit"
              disabled={isLoading}
              className="bg-primary w-1/6 justify-center rounded px-3 py-1.5 text-xs font-medium text-white hover:bg-opacity-90 disabled:opacity-50"
            >
              {isLoading ? "Зареждане..." : "Покажи"}
            </button>
          </div>
        </form>

        {documents.length > 0 && (
          <div className="p-3">
            <DocumentsPPPDataGrid
              documents={documents}
              isLoading={isLoading}
              printMetadata={printMetadata}
              printFooterMetadata={printFooterMetadata}
            />{" "}
          </div>
        )}

        {/* Empty State */}
        {documents.length === 0 && !isLoading && (
          <div className="flex items-center justify-center p-8">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Няма намерени документи.
            </p>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center p-8">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Зареждане на документи...
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default DocumentsPPP;
